import {
  type MutableRefObject,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useGetList, useRecordContext } from "ra-core";
import {
  Download,
  Maximize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ZoomRecording } from "../types";
import { formatTime } from "./formatTime";
import {
  type MeetingClock,
  meetingSpan,
  positionAt,
  type RecordingPart,
} from "./meetingTimeline";

const VIEW_LABELS: Record<string, string> = {
  shared_screen_with_speaker_view: "Screen + Speaker",
  active_speaker: "Active Speaker",
  gallery_view: "Gallery",
  shared_screen_with_gallery_view: "Screen + Gallery",
  shared_screen: "Screen Only",
  audio_only: "Audio only",
};

// Zoom appends "(CC)" to a view when captions are burned in. The layout is the
// same, so it gets the same label instead of falling through to the raw string.
export function viewLabel(recordingType: string): string {
  return (
    VIEW_LABELS[recordingType] ??
    VIEW_LABELS[recordingType.replace(/\(CC\)$/, "")] ??
    recordingType
  );
}

const VIDEO_FILE_TYPES = new Set(["MP4"]);
// A phone call, and any meeting recorded without video, produces audio alone.
// M4A is what Zoom cloud recordings use; MP3 is what Zoom Phone produces.
const AUDIO_FILE_TYPES = new Set(["M4A", "MP3"]);

type PlayableLike = {
  file_type: string;
  dropbox_shared_link?: string | null;
};

export function isAudioOnly(fileType: string): boolean {
  return AUDIO_FILE_TYPES.has(fileType);
}

/** Audio or video. Excludes TRANSCRIPT, CC, CHAT and TIMELINE rows. */
export function isMedia(fileType: string): boolean {
  return VIDEO_FILE_TYPES.has(fileType) || AUDIO_FILE_TYPES.has(fileType);
}

/** Which kind of track a playable row is, matching the view's state columns. */
export function trackKind(r: {
  file_type: string;
  recording_type?: string | null;
}): "screen" | "camera" | "audio" {
  if (isAudioOnly(r.file_type)) return "audio";
  return (r.recording_type ?? "").startsWith("shared_screen")
    ? "screen"
    : "camera";
}

/**
 * The track to start on when a link names a kind: the first playable of that
 * kind, or the usual default (video first) when the meeting has none.
 */
export function pickInitialTrack<
  T extends PlayableLike & { recording_type?: string | null },
>(playable: T[], kind?: "screen" | "camera" | "audio"): T | undefined {
  return (
    (kind ? playable.find((r) => trackKind(r) === kind) : undefined) ??
    playable[0]
  );
}

/**
 * Which track Play should land on for a kind: stay on the current track when it
 * is already that kind (so Play resumes rather than jumping between layouts),
 * otherwise the first track of that kind. Undefined when there is none.
 */
export function trackToSwitchTo<
  T extends PlayableLike & {
    id: number | string;
    recording_type?: string | null;
  },
>(
  playable: T[],
  currentId: number | string | null | undefined,
  kind: "screen" | "camera" | "audio",
): T["id"] | undefined {
  const current = playable.find((r) => r.id === currentId);
  if (current && trackKind(current) === kind) return current.id;
  return playable.find((r) => trackKind(r) === kind)?.id;
}

/** Commands a page can send the player, from outside its own controls. */
export type PlayerControl = {
  /** Switch to a track of this kind, keeping the position, and play it. */
  playKind: (kind: "screen" | "camera" | "audio") => void;
};

/**
 * The recordings a browser can actually play, video first.
 *
 * Ordering is not cosmetic: most meetings carry both an MP4 and an M4A, and the
 * first entry becomes the default track. Sorting video ahead of audio keeps
 * those meetings on the video they have always defaulted to. Array.sort is
 * stable, so the recording_start order within each group survives.
 */
export function playableRecordings<T extends PlayableLike>(
  recordings: T[] | undefined,
): T[] {
  return (recordings ?? [])
    .filter((r) => !!r.dropbox_shared_link && isMedia(r.file_type))
    .sort(
      (a, b) =>
        Number(isAudioOnly(a.file_type)) - Number(isAudioOnly(b.file_type)),
    );
}

function streamUrl(link: string): string {
  return link.replace("dl=0", "raw=1");
}

/**
 * A Dropbox link that saves the file instead of previewing it.
 *
 * `dl=1` makes Dropbox send `Content-Disposition: attachment`. The HTML
 * `download` attribute is ignored cross-origin, so the query parameter is what
 * actually does the work here.
 */
export function downloadUrl(link: string): string {
  return link.replace("dl=0", "dl=1");
}

/**
 * How long after a meeting ends its media can still plausibly show up.
 *
 * Zoom's upload lands well after the meeting row does. In production the median
 * gap between a `zoom_meetings` row and its first media row is about 16 minutes,
 * and 22 of 25 recorded meetings took over a minute. The longest observed wait
 * was roughly 45 minutes, so 90 gives that generous headroom.
 *
 * KEEP IN STEP with `has_content` in supabase/schemas/03_views.sql, which lists
 * a meeting for the same 90 minutes measured from the same clock
 * (meetingClockRef). Changing one without the other badges meetings the list no
 * longer shows, or lists them without a badge.
 */
export const MEDIA_ARRIVAL_WINDOW_MS = 90 * 60 * 1000;
const MEDIA_POLL_MS = 30 * 1000;

/**
 * Whether a meeting is young enough that its recording could still be coming.
 *
 * This is the stop condition for polling, and it has to be the meeting's age
 * rather than "media is still missing". Meetings that were never recorded at all
 * never grow media rows, and production holds 18 of them, so keying on absence
 * would poll for as long as the tab stayed open.
 */
export function mediaMayStillArrive(
  meetingEndTime: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!meetingEndTime) return false;
  const ended = Date.parse(meetingEndTime);
  if (Number.isNaN(ended)) return false;
  return now - ended < MEDIA_ARRIVAL_WINDOW_MS;
}

/**
 * The timestamp to measure the wait from.
 *
 * `meeting_end_time` is set by the meeting.ended webhook, which does not always
 * fire: 31 of 122 production meetings have a start and no end. Falling back to
 * the start keeps the pending state working for those instead of dropping them
 * straight back to "not recorded". The fallback is measured from earlier in the
 * meeting, so on a long call the window closes sooner than it should, which
 * errs the same way the old code did rather than adding a new failure.
 */
export function meetingClockRef(meeting: {
  meeting_end_time?: string | null;
  meeting_start_time?: string | null;
}): string | null {
  return meeting.meeting_end_time ?? meeting.meeting_start_time ?? null;
}

export function useRecordings(
  meetingRef: number | undefined,
  meetingEndTime?: string | null,
) {
  return useGetList<ZoomRecording>(
    "zoom_recordings",
    {
      filter: { meeting_ref: meetingRef },
      sort: { field: "recording_start", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    {
      enabled: !!meetingRef,
      // Without this a page opened during the upload gap never corrects itself:
      // nothing else in atomic-crm refetches, so the panel keeps its first
      // answer until the user thinks to reload. That is how meeting 339 told
      // Bill his session was not recorded while the MP4 was still on its way.
      refetchInterval: (query) => {
        const raw = query.state.data as unknown;
        const rows = (
          Array.isArray(raw) ? raw : (raw as { data?: ZoomRecording[] })?.data
        ) as ZoomRecording[] | undefined;
        if (rows?.some((r) => isMedia(r.file_type))) return false;
        return mediaMayStillArrive(meetingEndTime) ? MEDIA_POLL_MS : false;
      },
    },
  );
}

/** A recorded part of the meeting with the tracks a browser can play in it. */
export type PlayerPart<T extends PlayableLike & RecordingRow> =
  RecordingPart<T> & {
    tracks: T[];
  };

type RecordingRow = {
  id: number | string;
  recording_type: string;
  file_type: string;
  recording_start?: string | null;
  recording_end?: string | null;
  transcript_content?: string | null;
};

/**
 * The parts the player can play, in order, each with its playable tracks.
 * Parts come from the meeting span, so their offsets are meeting time; a part
 * with nothing playable is left out and the rest are renumbered.
 */
export function playerParts<T extends PlayableLike & RecordingRow>(
  parts: RecordingPart<T>[],
): PlayerPart<T>[] {
  return parts
    .map((p) => ({ ...p, tracks: playableRecordings(p.rows) }))
    .filter((p) => p.tracks.length > 0)
    .map((p, index) => ({ ...p, index }));
}

/**
 * The layouts to offer, once each and video first. A layout is a recording
 * type; with several parts the same layout exists once per part, and listing it
 * per part is what made meeting 406 show "Screen + Speaker" three times.
 */
export function trackLayouts(playable: { recording_type: string }[]): string[] {
  return [...new Set(playable.map((r) => r.recording_type))];
}

/**
 * The recorded stretches as percentages of the meeting, for the progress bar.
 * A part with no end time is drawn up to the next part, or to the meeting end.
 */
export function recordedStretches(
  parts: { offset: number; duration: number | null }[],
  total: number,
): { left: number; width: number }[] {
  if (total <= 0) return [];
  return parts.map((p, i) => {
    const end =
      p.duration != null
        ? p.offset + p.duration
        : (parts[i + 1]?.offset ?? total);
    const left = Math.min(100, (p.offset / total) * 100);
    return {
      left,
      width: Math.max(
        0,
        Math.min(100 - left, ((end - p.offset) / total) * 100),
      ),
    };
  });
}

export const RecordingPlayer = ({
  videoRef,
  onActiveChange,
  meetingRef,
  meetingEndTime,
  meeting,
  initialKind,
  controlRef,
  emptyMessage,
}: {
  /** Receives the meeting clock, which runs from the meeting's start to its end. */
  videoRef: RefObject<MeetingClock | null>;
  onActiveChange?: () => void;
  meetingRef?: number;
  meetingEndTime?: string | null;
  /**
   * The meeting's start and end, which the timeline spans. Defaults to the
   * record in context, which is the meeting on the meeting pages; a task has to
   * pass it.
   */
  meeting?: {
    meeting_start_time?: string | null;
    meeting_end_time?: string | null;
  } | null;
  /** Start on this kind of track, from a meeting link's `asset`. */
  initialKind?: "screen" | "camera" | "audio";
  /**
   * Filled with the player's commands while it has something to play. The
   * asset bar calls these straight from its click handler, which keeps play()
   * inside the user's gesture where browsers allow it.
   */
  controlRef?: MutableRefObject<PlayerControl | null>;
  /**
   * The shared message to show when nothing is playable, from the view's state
   * (see assetMessage). Without it the player works the state out from its own
   * rows, as it does inside a task.
   */
  emptyMessage?: string | null;
}) => {
  const record = useRecordContext<{
    id?: number;
    meeting_ref?: number | null;
    meeting_end_time?: string | null;
    meeting_start_time?: string | null;
  }>();
  const id = meetingRef ?? (record?.meeting_ref as number | undefined);
  // Present when the record in context is the meeting itself. Rendered from a
  // task it is not, and the panel then falls back to the old two-state answer
  // rather than guessing at an age it cannot see.
  const endedAt = meetingEndTime ?? (record ? meetingClockRef(record) : null);
  const { data: recordings } = useRecordings(id, endedAt);

  const playable = playableRecordings(recordings);
  const span = meetingSpan(meeting ?? record, recordings);
  const parts = playerParts(span.parts);
  const layouts = trackLayouts(playable);

  const [chosenLayout, setChosenLayout] = useState<string | null>(null);
  const [activePart, setActivePart] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [displayTime, setDisplayTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  // Where the seek bar is being dragged to, shown in the time readout at once
  // rather than when the video catches up.
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  // Bumped on download progress so the loaded indicator redraws.
  const [, setBuffered] = useState(0);

  const elMap = useRef<Map<number, HTMLVideoElement>>(new Map());
  const listeners = useRef<Set<() => void>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const playingRef = useRef(false);
  // Meeting time while in black, where no element is keeping it.
  const gapTime = useRef(0);
  const gapFrame = useRef({ raf: 0, last: 0 });
  const positioned = useRef(false);
  const shownQuarter = useRef(-1);

  // The linked kind is chosen once, the first time any track is playable. The
  // recordings load after mount, and re-choosing on every refetch would undo a
  // view the user has since switched to by hand.
  const linkedLayout = useRef<string | null>(null);
  if (initialKind && linkedLayout.current == null && playable.length > 0) {
    linkedLayout.current =
      pickInitialTrack(playable, initialKind)?.recording_type ?? null;
  }
  const layout =
    [chosenLayout, linkedLayout.current].find(
      (l): l is string => l != null && layouts.includes(l),
    ) ?? layouts[0];

  // What the clock reads. Refs, not state, so a seek followed at once by play()
  // (which is how every caller uses it) acts on where it just moved to.
  const live = useRef<{
    parts: PlayerPart<ZoomRecording>[];
    layout: string | undefined;
    activePart: number | null;
    duration: number;
  }>({ parts, layout, activePart, duration: span.duration });
  live.current = {
    parts,
    layout: live.current.layout === layout ? live.current.layout : layout,
    activePart: live.current.activePart,
    duration: span.duration,
  };

  const trackIn = (
    part: PlayerPart<ZoomRecording> | undefined,
    key: string | undefined,
  ) => part?.tracks.find((t) => t.recording_type === key) ?? part?.tracks[0];
  const activeElement = () => {
    const s = live.current;
    if (s.activePart == null) return undefined;
    const track = trackIn(s.parts[s.activePart], s.layout);
    return track ? elMap.current.get(track.id as number) : undefined;
  };

  const now = () => {
    const s = live.current;
    if (s.activePart == null) return gapTime.current;
    const offset = s.parts[s.activePart]?.offset ?? 0;
    return offset + (activeElement()?.currentTime ?? 0);
  };

  const emit = () => {
    listeners.current.forEach((l) => l());
    const quarter = Math.floor(now() * 4);
    if (quarter !== shownQuarter.current) {
      shownQuarter.current = quarter;
      setDisplayTime(quarter / 4);
    }
  };

  const stopGapTimer = () => {
    cancelAnimationFrame(gapFrame.current.raf);
    gapFrame.current.raf = 0;
  };

  const setPlayingBoth = (value: boolean) => {
    playingRef.current = value;
    setPlaying(value);
  };

  // Move to a meeting time: into a part at the matching moment, or into black.
  const moveTo = (target: number, play: boolean) => {
    const s = live.current;
    const t = Math.max(0, Math.min(target, s.duration));
    const pos = positionAt(s.parts, t);
    const before = s.activePart;
    stopGapTimer();
    if (pos.kind === "part") {
      if (before !== pos.part) activeElement()?.pause();
      live.current = { ...live.current, activePart: pos.part };
      const el = activeElement();
      if (el) {
        el.currentTime = pos.local;
        if (play) el.play().catch(() => {});
      }
    } else {
      activeElement()?.pause();
      live.current = { ...live.current, activePart: null };
      gapTime.current = t;
      if (play) startGapTimer();
    }
    if (before !== live.current.activePart) {
      setActivePart(live.current.activePart);
      onActiveChange?.();
    }
    emit();
  };

  // Black plays in real time: the clock keeps running until the next part
  // starts or the meeting ends. Long gaps are not skipped (Sam, 2026-09-16).
  const startGapTimer = () => {
    stopGapTimer();
    gapFrame.current.last = performance.now();
    const tick = (ts: number) => {
      gapTime.current += Math.max(0, ts - gapFrame.current.last) / 1000;
      gapFrame.current.last = ts;
      const s = live.current;
      if (gapTime.current >= s.duration) {
        gapTime.current = s.duration;
        stopGapTimer();
        setPlayingBoth(false);
        emit();
        return;
      }
      if (positionAt(s.parts, gapTime.current).kind === "part") {
        moveTo(gapTime.current, true);
        return;
      }
      emit();
      gapFrame.current.raf = requestAnimationFrame(tick);
    };
    gapFrame.current.raf = requestAnimationFrame(tick);
  };

  const play = () => {
    const s = live.current;
    setPlayingBoth(true);
    if (now() >= s.duration - 0.05) {
      moveTo(0, true);
      return;
    }
    const el = activeElement();
    if (s.activePart != null && el) el.play().catch(() => {});
    else startGapTimer();
  };

  const pause = () => {
    setPlayingBoth(false);
    activeElement()?.pause();
    stopGapTimer();
    emit();
  };

  // One clock for the whole meeting, stable for the player's lifetime, so a
  // listener attached once keeps hearing time updates across parts and black.
  const clock = useRef<MeetingClock | null>(null);
  if (!clock.current) {
    clock.current = {
      get currentTime() {
        return now();
      },
      set currentTime(t: number) {
        moveTo(t, playingRef.current);
      },
      get paused() {
        return !playingRef.current;
      },
      // Black is always ready; a seek into it should not wait for a download.
      get readyState() {
        return live.current.activePart == null
          ? 4
          : (activeElement()?.readyState ?? 0);
      },
      play: () => {
        play();
        return Promise.resolve();
      },
      pause: () => pause(),
      addEventListener: (_type, listener) => {
        listeners.current.add(listener);
      },
      removeEventListener: (_type, listener) => {
        listeners.current.delete(listener);
      },
    };
  }

  // Place the clock at 0:00 the first time there is something to play. Runs
  // before the page's own effects (children first), so a link's `t` or a task's
  // moment still wins.
  useEffect(() => {
    if (positioned.current || parts.length === 0) return;
    positioned.current = true;
    moveTo(0, false);
    onActiveChange?.();
  });

  useEffect(() => {
    elMap.current.forEach((el) => {
      el.volume = volume;
      el.muted = muted;
    });
  }, [volume, muted, playable.length]);

  useEffect(() => stopGapTimer, []);

  // Nothing playable is not the same as nothing happened. Say which, otherwise
  // an empty panel reads as "the recording was lost" when the meeting was
  // simply never recorded.
  if (playable.length === 0) {
    if (controlRef) controlRef.current = null;
    if (emptyMessage) {
      return (
        <div className="border rounded-lg h-full flex items-center justify-center text-muted-foreground text-sm p-4 text-center">
          {emptyMessage}
        </div>
      );
    }
    // Three states, not two. Zero media rows used to fall through to "not
    // recorded", which asserts absence during the window when Zoom simply has
    // not delivered yet, and that is the whole of LCATOMICZO-73.
    //
    // Only audio and video count here. A transcript or caption file still in
    // flight says nothing about whether there is a recording to play.
    const stillUploading = (recordings ?? []).some(
      (r) => isMedia(r.file_type) && !r.dropbox_shared_link,
    );
    const pendingFromZoom =
      !stillUploading &&
      !(recordings ?? []).some((r) => isMedia(r.file_type)) &&
      mediaMayStillArrive(endedAt);
    return (
      <div className="border rounded-lg h-full flex items-center justify-center text-muted-foreground text-sm p-4 text-center">
        {stillUploading
          ? "Recording is still uploading…"
          : pendingFromZoom
            ? "Waiting for the recording from Zoom. This usually takes about 15 minutes and the page will update on its own."
            : "This meeting was not recorded."}
      </div>
    );
  }

  (videoRef as MutableRefObject<MeetingClock | null>).current = clock.current;
  const activeTrack =
    activePart == null ? undefined : trackIn(parts[activePart], layout);

  // One link per kind per part rather than one per track. A meeting can carry
  // several video layouts, and offering four near-identical downloads helps
  // nobody; Bill asked for the audio specifically, so that one is always offered
  // when it exists.
  const downloads = parts.flatMap((p) =>
    [
      p.tracks.find((r) => !isAudioOnly(r.file_type)),
      p.tracks.find((r) => isAudioOnly(r.file_type)),
    ]
      .filter((r): r is ZoomRecording => !!r)
      .map((r) => {
        const kind = isAudioOnly(r.file_type) ? "Audio" : "Video";
        return {
          row: r,
          label:
            parts.length > 1
              ? `Part ${p.index + 1} ${kind.toLowerCase()}`
              : kind,
        };
      }),
  );

  const handleLayoutChange = (nextLayout: string) => {
    const before = activeElement();
    live.current = { ...live.current, layout: nextLayout };
    setChosenLayout(nextLayout);
    const after = activeElement();
    if (after && after !== before) {
      before?.pause();
      after.currentTime = before?.currentTime ?? 0;
      if (playingRef.current) after.play().catch(() => {});
    }
    onActiveChange?.();
  };

  if (controlRef) {
    controlRef.current = {
      playKind: (kind) => {
        const current = trackIn(parts[activePart ?? 0], layout);
        const target = trackToSwitchTo(playable, current?.id, kind);
        const targetLayout = playable.find(
          (r) => r.id === target,
        )?.recording_type;
        if (targetLayout == null) return;
        if (targetLayout !== layout) handleLayoutChange(targetLayout);
        play();
      },
    };
  }

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      containerRef.current?.requestFullscreen().catch(() => {});
    }
  };

  const total = span.duration;
  const stretches = recordedStretches(parts, total);
  // What has been downloaded, on the meeting timeline: each part's current view
  // (only it is ever fetched), its buffered ranges moved by the part's offset.
  const loaded: Stretch[] =
    total <= 0
      ? []
      : parts.flatMap((p) => {
          const track = trackIn(p, layout);
          const el = track ? elMap.current.get(track.id as number) : undefined;
          if (!el) return [];
          return Array.from({ length: el.buffered.length }, (_, i) => {
            const start = p.offset + el.buffered.start(i);
            const end = p.offset + el.buffered.end(i);
            const left = Math.min(100, (start / total) * 100);
            return {
              left,
              width: Math.max(
                0,
                Math.min(100 - left, ((end - start) / total) * 100),
              ),
            };
          });
        });
  const inBlack = activePart == null;
  const audioOnly = activeTrack ? isAudioOnly(activeTrack.file_type) : false;

  return (
    <div
      ref={containerRef}
      className="border rounded-lg overflow-hidden h-full flex flex-col relative bg-background"
    >
      <div className="flex items-center gap-2 p-2 bg-muted/50 border-b z-10 relative">
        {layouts.length > 1 && (
          <>
            <span className="text-sm font-medium text-muted-foreground">
              View:
            </span>
            <select
              value={layout}
              onChange={(e) => handleLayoutChange(e.target.value)}
              className="text-sm border rounded px-2 py-1 bg-background"
            >
              {layouts.map((l) => (
                <option key={l} value={l}>
                  {viewLabel(l)}
                </option>
              ))}
            </select>
          </>
        )}
        <div className="ml-auto">
          {/* Always a menu, even with one entry. A button that sometimes opens a
              menu and sometimes downloads straight away teaches nothing about
              what the next click will do. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 cursor-pointer"
                aria-label="Download recording"
                title="Download recording"
              >
                <Download className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {downloads.map(({ row, label }) => (
                <DropdownMenuItem key={row.id as number} asChild>
                  <a
                    href={downloadUrl(row.dropbox_shared_link as string)}
                    download
                    className="cursor-pointer"
                  >
                    {label}
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* The stage: the part playing now, or black where nothing was recorded. */}
      <div
        className="relative flex-1 min-h-0 bg-black cursor-pointer"
        onClick={() => (playing ? pause() : play())}
      >
        {playable.map((r) => {
          const isActive = r.id === activeTrack?.id;
          return (
            <video
              key={r.id as number}
              ref={(el) => {
                if (el) {
                  el.volume = volume;
                  el.muted = muted;
                  elMap.current.set(r.id as number, el);
                } else {
                  elMap.current.delete(r.id as number);
                }
              }}
              onProgress={() => setBuffered((n) => n + 1)}
              onTimeUpdate={(e) => {
                if (activeElement() === e.currentTarget) emit();
              }}
              // A part ending moves the clock on to whatever follows it, black
              // or the next part, and keeps playing.
              onEnded={(e) => {
                const el = e.currentTarget;
                const s = live.current;
                if (s.activePart == null || activeElement() !== el) return;
                const part = s.parts[s.activePart];
                const next = s.parts[s.activePart + 1];
                const byDb =
                  part.duration != null
                    ? part.offset + part.duration
                    : (next?.offset ?? 0);
                moveTo(
                  Math.max(part.offset + (el.duration || 0), byDb) + 0.001,
                  playingRef.current,
                );
              }}
              preload={isActive ? "auto" : "none"}
              playsInline
              src={streamUrl(r.dropbox_shared_link!)}
              className={cn(
                "absolute inset-0 w-full h-full object-contain pointer-events-none",
                isActive && !isAudioOnly(r.file_type)
                  ? "opacity-100"
                  : "opacity-0",
              )}
            />
          );
        })}
        {(inBlack || audioOnly) && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-white/50 pointer-events-none">
            {inBlack ? "Not recorded" : "Audio only"}
          </div>
        )}
      </div>

      {/* Controls. The browser's own only know one file, and this timeline is
          the whole meeting, so the player draws its own. */}
      <div className="flex items-center gap-2 px-2 py-1 border-t bg-muted/50">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label={playing ? "Pause" : "Play"}
          onClick={() => (playing ? pause() : play())}
        >
          {playing ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4" />
          )}
        </Button>
        <span className="text-xs font-mono tabular-nums text-muted-foreground whitespace-nowrap">
          {formatTime(scrubTime ?? displayTime)} / {formatTime(total)}
        </span>
        <SeekBar
          total={total}
          time={Math.min(displayTime, total)}
          stretches={stretches}
          onSeek={(t) => moveTo(t, playingRef.current)}
          onScrub={setScrubTime}
          loaded={loaded}
          recordedAt={(t) => positionAt(parts, t).kind === "part"}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label={muted ? "Unmute" : "Mute"}
          onClick={() => setMuted((m) => !m)}
        >
          {muted || volume === 0 ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </Button>
        <input
          type="range"
          aria-label="Volume"
          min={0}
          max={1}
          step={0.05}
          value={muted ? 0 : volume}
          onChange={(e) => {
            setVolume(Number(e.target.value));
            setMuted(false);
          }}
          className="w-16 accent-primary"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Fullscreen"
          onClick={toggleFullscreen}
        >
          <Maximize className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

type Stretch = { left: number; width: number };

// The meeting timeline. Drawn and handled here rather than with a range input:
// a native slider maps the pointer over its width minus the thumb, so the dot
// sat off the pointer. Layers, back to front: unrecorded (track), recorded,
// downloaded, played.
function SeekBar({
  total,
  time,
  stretches,
  onSeek,
  onScrub,
  loaded,
  recordedAt,
}: {
  total: number;
  time: number;
  stretches: Stretch[];
  onSeek: (t: number) => void;
  onScrub: (t: number | null) => void;
  loaded: Stretch[];
  recordedAt: (t: number) => boolean;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const timeAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || total <= 0) return 0;
    const f = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return f * total;
  };
  const pct = (t: number) => (total > 0 ? Math.min(100, (t / total) * 100) : 0);

  const shown = dragging ?? time;
  const pointed = dragging ?? hover;

  return (
    <div
      ref={barRef}
      role="slider"
      tabIndex={0}
      aria-label="Meeting position"
      aria-valuemin={0}
      aria-valuemax={Math.round(total)}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={formatTime(shown)}
      className="relative flex-1 h-5 flex items-center min-w-16 cursor-pointer touch-none outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const t = timeAt(e.clientX);
        setDragging(t);
        onScrub(t);
        onSeek(t);
      }}
      onPointerMove={(e) => {
        const t = timeAt(e.clientX);
        setHover(t);
        if (dragging != null) {
          setDragging(t);
          onScrub(t);
          onSeek(t);
        }
      }}
      onPointerUp={(e) => {
        if (dragging == null) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        setDragging(null);
        onScrub(null);
      }}
      onPointerLeave={() => setHover(null)}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 30 : 5;
        if (e.key === "ArrowRight") onSeek(Math.min(total, time + step));
        else if (e.key === "ArrowLeft") onSeek(Math.max(0, time - step));
        else if (e.key === "Home") onSeek(0);
        else if (e.key === "End") onSeek(total);
        else return;
        e.preventDefault();
      }}
    >
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-foreground/15 overflow-hidden pointer-events-none">
        {stretches.map((s, i) => (
          <div
            key={i}
            className="absolute inset-y-0 bg-primary/35"
            style={{ left: `${s.left}%`, width: `${s.width}%` }}
          />
        ))}
        {loaded.map((s, i) => (
          <div
            key={`l${i}`}
            className="absolute inset-y-0 bg-primary/60"
            style={{ left: `${s.left}%`, width: `${s.width}%` }}
          />
        ))}
        <div
          className="absolute inset-y-0 left-0 bg-primary"
          style={{ width: `${pct(shown)}%` }}
        />
      </div>
      <div
        className="absolute h-3 w-3 -ml-1.5 rounded-full bg-primary shadow pointer-events-none"
        style={{ left: `${pct(shown)}%` }}
      />
      {pointed != null && (
        <span
          className="absolute bottom-full mb-2 -translate-x-1/2 pointer-events-none whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-xs font-mono tabular-nums text-background shadow"
          style={{
            left: `clamp(2.5rem, ${pct(pointed)}%, calc(100% - 2.5rem))`,
          }}
        >
          {formatTime(pointed)}
          {recordedAt(pointed) ? "" : " · not recorded"}
        </span>
      )}
    </div>
  );
}
